import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Modal, Stack } from '@mui/material'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import requestStore from '../../stores/requestStore'
import objectStore from '../../stores/objectStore'
import i18nStore from '../../stores/i18nStore'
import SaleTypeSelectorRequest from '../object/SaleTypeSelectorRequest'
import CategorySelectorRequest from '../object/CategorySelectorRequest'
import AddressRequest from '../object/AddressRequest'
import ExtraFilters from '../object/ExtraFilters'

function parseSubcategoryIds(value) {
  if (Array.isArray(value)) return value.map(Number).filter(Number.isSafeInteger)
  if (typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isSafeInteger) : []
  } catch { return [] }
}

function selectedSubcategories(filter) {
  const ids = new Set(parseSubcategoryIds(filter?.subcategories))
  return objectStore.categories.flatMap(category => category.subcategories || [])
    .filter(subcategory => ids.has(Number(subcategory.id)))
}

function RequestCriteriaEditor({ request, onCancel, onSaved }) {
  const filter = useMemo(() => request?.filter || {}, [request?.filter])
  const [addressShort, setAddressShort] = useState(filter.addressShort || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [extraOpen, setExtraOpen] = useState(false)
  const selection = useMemo(() => selectedSubcategories(filter), [filter])

  useEffect(() => {
    const activeCategoryId = selection.length
      ? objectStore.categories.find(category => (category.subcategories || []).some(subcategory => Number(subcategory.id) === Number(selection[0].id)))?.id || null
      : null
    requestStore.filterForRequest = {
      saleType: filter.saleType || request?.saleType || 'sale',
      activeCategoryId,
      selectedSubcategories: selection,
      rentType: [],
    }
    requestStore.extraFilter = { ...(filter.extraFilter || {}) }
    requestStore.address = { ...(filter.address || {}) }
    requestStore.isAddressSelected = Boolean(filter.address && Object.keys(filter.address).length)
    setAddressShort(filter.addressShort || '')
  }, [filter, request?.saleType, selection])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const result = await requestStore.updateRequestCriteria(request.id, {
        commonFilter: toJS(requestStore.filterForRequest),
        extraFilters: toJS(requestStore.extraFilter),
        address: toJS(requestStore.address),
        addressShort,
        countryCodeISO: request.countryCodeISO || i18nStore.currentCountry,
      })
      onSaved(result)
    } catch (saveError) {
      setError(saveError.response?.data?.message || 'Не удалось обновить критерии заявки')
    } finally { setSaving(false) }
  }

  const selected = requestStore.filterForRequest.selectedSubcategories
  return <Box sx={{ mt: 2, p: 2, border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
    <Stack spacing={2}>
      <Alert severity="info">Сохранение запустит пересчёт автоматических совпадений в фоне.</Alert>
      {error && <Alert severity="error">{error}</Alert>}
      <SaleTypeSelectorRequest />
      <CategorySelectorRequest />
      <AddressRequest initialAddress={addressShort} onAddressChange={setAddressShort} />
      {selected.length === 1 && <Button variant="outlined" onClick={() => setExtraOpen(true)}>Расширенный фильтр</Button>}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Сохранить критерии</Button>
      </Stack>
    </Stack>
    <Modal open={extraOpen} onClose={() => setExtraOpen(false)}>
      <Box sx={{ position: 'absolute', top: '5%', left: '50%', transform: 'translateX(-50%)', width: { xs: '96%', md: '70%' }, maxHeight: '90vh', bgcolor: 'background.paper', p: 2, borderRadius: 2 }}>
        {selected.length === 1 && <ExtraFilters categoryId={selected[0].id} source="request" onClose={() => setExtraOpen(false)} />}
      </Box>
    </Modal>
  </Box>
}

export default observer(RequestCriteriaEditor)
